import { useState } from "react";
import { Phone, User, CheckCircle, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  CallbackRequestError,
  submitCallbackRequest,
} from "@/services/callback";
import { trackCallbackLead } from "@/utils/trackLead";

const NAME_MAX_LENGTH = 80;

export const CallbackForm = ({
  variant = "section",
  source = "homepage",
}) => {
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const idPrefix = variant === "inline" ? "inline" : "cb";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!/^[6-9][0-9]{9}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }

    setIsLoading(true);
    try {
      await submitCallbackRequest({
        name: formData.name,
        phone: formData.phone,
        source,
        honeypot,
      });
      trackCallbackLead(source);
      setIsSubmitted(true);
      toast.success("Callback request submitted!");
    } catch (error) {
      const message =
        error instanceof CallbackRequestError
          ? error.message
          : "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({ name: "", phone: "" });
    setHoneypot("");
    setIsSubmitted(false);
  };

  const honeypotField = (
    <div
      className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
      aria-hidden="true"
    >
      <label htmlFor={`${idPrefix}-company-website`}>Company website</label>
      <input
        id={`${idPrefix}-company-website`}
        name="company_website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="bg-white rounded-2xl p-6 border border-[#e2e8f0] shadow-sm">
        <h3 className="text-lg font-bold text-[#0a2a43] mb-4">Request a Callback</h3>
        {isSubmitted ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <p className="font-semibold text-[#0a2a43]">Thank you!</p>
            <p className="text-sm text-[#4a6680] mt-1">We will contact you shortly.</p>
            <Button onClick={handleReset} variant="outline" className="mt-4 text-sm">
              Submit Another
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 relative">
            {honeypotField}
            <div>
              <Label htmlFor="inline-name" className="text-sm text-[#4a6680] mb-1.5 block">Name</Label>
              <Input
                id="inline-name"
                placeholder="Your name"
                value={formData.name}
                maxLength={NAME_MAX_LENGTH}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border-[#e2e8f0] focus:border-[#ff9900] focus:ring-[#ff9900]/20"
              />
            </div>
            <div>
              <Label htmlFor="inline-phone" className="text-sm text-[#4a6680] mb-1.5 block">Phone</Label>
              <Input
                id="inline-phone"
                placeholder="10-digit number"
                inputMode="numeric"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) })}
                className="border-[#e2e8f0] focus:border-[#ff9900] focus:ring-[#ff9900]/20"
              />
            </div>
            <Button type="submit" disabled={isLoading} className="w-full bg-[#ff9900] hover:bg-[#e68a00] text-white font-semibold py-5 rounded-xl">
              {isLoading ? "Submitting..." : "Request Callback"}
            </Button>
          </form>
        )}
      </div>
    );
  }

  return (
    <section id="callback" className="py-24 bg-[#0a2a43] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff9900]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#ff9900]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#ff9900] font-semibold text-sm uppercase tracking-wider">
              Get In Touch
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 mb-6">
              Request a Callback
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-8">
              Need help choosing the right automation solution? Fill in your details
              and our team will call you back to discuss your requirements.
            </p>
            <div className="flex flex-col gap-4">
              {[
                "Free consultation on your automation needs",
                "Quick response within 2 hours",
                "Expert technical guidance",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#ff9900]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-3.5 h-3.5 text-[#ff9900]" />
                  </div>
                  <span className="text-white/70 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-10">
            {isSubmitted ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Thank you!</h3>
                <p className="text-white/60 text-lg">We will contact you shortly.</p>
                <Button onClick={handleReset} variant="outline" className="mt-6 border-white/20 text-white hover:bg-white/10">
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 relative">
                {honeypotField}
                <div>
                  <Label htmlFor="cb-name" className="text-white/80 text-sm mb-2 block font-medium">
                    Your Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="cb-name"
                      placeholder="Enter your name"
                      value={formData.name}
                      maxLength={NAME_MAX_LENGTH}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#ff9900] focus:ring-[#ff9900]/20 h-12 rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cb-phone" className="text-white/80 text-sm mb-2 block font-medium">
                    Phone Number
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="cb-phone"
                      placeholder="10-digit phone number"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) })}
                      className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#ff9900] focus:ring-[#ff9900]/20 h-12 rounded-xl"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#ff9900] hover:bg-[#e68a00] text-white font-semibold py-6 text-base rounded-xl shadow-lg shadow-[#ff9900]/20 hover:shadow-xl transition-all duration-300"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      Request Callback
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
